import { useEffect } from "react";
import { Alert, Box, Button, Input } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { omit, pick } from "lodash";
import * as brandSchemaFields from "utils/schemas/brand";
import { useBrandMutation } from "utils/apollo/hooks/brand";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import { BrandPatch } from "__generated__/gql/graphql";
import Form from "components/Shared/Forms/Form";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import UrlInput from "components/Shared/Forms/UrlInput";
import {
  getPostgraphileErrorMatchersForFields,
  makePostgraphileErrorProcessor,
} from "utils/PostgraphileErrorExtensions";
import { assertBrandId } from "utils/assertions";
import ContactEmailLink from "components/Shared/ContactEmailLink";
import FormFieldWrapper from "components/Shared/Forms/FormFieldWrapper";
import { ParentBrandText } from "components/Shared/ParentBrandAlert";

const brandFragment = gql(/* GraphQL */ `
  fragment BrandSettingsFields on Brand {
    id
    name
    slug
    website
    parentId
    parentName
    ...ParentBrandText
  }
`);

const mutation = gql(/* GraphQL */ `
  mutation UpdateBrandSettings($brandInput: UpdateBrandInput!) {
    updateBrand(input: $brandInput) {
      brand {
        ...BrandSettingsFields
      }
    }
  }
`);

const BRAND_FIELDS = ["name", "slug", "website"] as const;

type Inputs = Required<Pick<BrandPatch, typeof BRAND_FIELDS[number]>>;

const schema: yup.ObjectSchema<Inputs> = yup.object().shape({
  name: brandSchemaFields.name,
  slug: brandSchemaFields.slug,
  website: brandSchemaFields.website,
});

const { ControlledFormField, FormField } = createFormFieldComponents<Inputs>({
  schema,
});

const processApolloError = makePostgraphileErrorProcessor<Inputs>(
  getPostgraphileErrorMatchersForFields(BRAND_FIELDS, { type: "Brand" }),
);

type Props = {
  brand: FragmentType<typeof brandFragment> | null | undefined;
  isLoading?: boolean;
};

export default function BrandSettingsForm({
  brand: brandFragmentData,
  isLoading,
}: Props) {
  const formContext = useForm<Inputs>({
    resolver: yupResolver(schema),
  });
  const {
    formState: { isDirty },
    reset,
  } = formContext;

  const brand = getFragmentData(brandFragment, brandFragmentData);
  const brandId = brand?.id;

  const [updateAccount, { loading: isSaving }] = useBrandMutation(mutation);

  useEffect(() => {
    if (brand) {
      reset(pick(brand, BRAND_FIELDS));
    }
  }, [brand, reset]);

  const [handleValidSubmit] = useCreateValidSubmitHandler(
    async (formData: Inputs) => {
      assertBrandId(brandId);
      return updateAccount({
        variables: {
          brandInput: { id: brandId, patch: omit(formData, ["slug"]) },
        },
      });
    },
    {
      successMessage: "Brand settings updated successfully.",
      processApolloError,
    },
  );

  return (
    <Form
      formContext={formContext}
      isLoaded={!!brandId && !isLoading}
      onValidSubmit={handleValidSubmit}
    >
      <FormField name="name" autoComplete="organization" />

      <FormField
        name="slug"
        helpText={
          <>
            Please contact <ContactEmailLink /> for help with changing this
            brand’s slug.
          </>
        }
        renderInput={(props) => (
          <Input
            {...props}
            fontFamily="mono"
            fontWeight="normal"
            spellCheck={false}
            readOnly
            isDisabled
          />
        )}
      />

      <ControlledFormField
        name="website"
        renderInput={({ field: props }) => (
          <UrlInput allowPath={false} {...props} />
        )}
      />

      {brand?.parentId && (
        <Alert mt={10}>
          <FormFieldWrapper
            label="Parent"
            my={0}
            helpText={<ParentBrandText brand={brand} />}
          >
            <Input value={brand?.parentName ?? ""} isDisabled />
          </FormFieldWrapper>
        </Alert>
      )}

      <Box mt={12} textAlign="right">
        <Button
          type="submit"
          colorScheme="pinkButton"
          size="md"
          disabled={isSaving || !isDirty}
          isLoading={isSaving}
          loadingText="Saving"
        >
          Save
        </Button>
      </Box>
    </Form>
  );
}
